import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, Users, ListTodo, ArrowLeftRight,
  BarChart3, Bell, Settings, LogOut, UserSquare2, Building2, Receipt,
  Tag, Sparkles, Mail, MapPin, BookMarked, Trophy,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import LibKicker from './ui/LibKicker';

const NAV_GROUPS = [
  {
    num: '01', label: 'Overview',
    items: [{ name: 'Dashboard', href: '/librarian', icon: LayoutDashboard, exact: true }],
  },
  {
    num: '02', label: 'Collection',
    items: [
      { name: 'Books', href: '/librarian/books', icon: BookOpen },
      { name: 'Authors', href: '/librarian/authors', icon: UserSquare2 },
      { name: 'Publishers', href: '/librarian/publishers', icon: Building2 },
      { name: 'Genres', href: '/librarian/genres', icon: Tag },
      { name: 'Conditions', href: '/librarian/conditions', icon: Sparkles },
      { name: 'Locations', href: '/librarian/locations', icon: MapPin },
    ],
  },
  {
    num: '03', label: 'Operations',
    items: [
      { name: 'Loans', href: '/librarian/loans', icon: ArrowLeftRight },
      { name: 'Members', href: '/librarian/members', icon: Users },
      { name: 'Penalties', href: '/librarian/penalties', icon: Receipt },
      { name: 'Overdue notices', href: '/librarian/overdue-notices', icon: Mail },
      { name: 'Book clubs', href: '/librarian/book-clubs', icon: BookMarked },
      { name: 'Challenges', href: '/librarian/challenges', icon: Trophy },
      { name: 'Tasks', href: '/librarian/tasks', icon: ListTodo },
    ],
  },
  {
    num: '04', label: 'System',
    items: [
      { name: 'Analytics', href: '/librarian/analytics', icon: BarChart3 },
      { name: 'Notifications', href: '/librarian/notifications', icon: Bell },
      { name: 'Settings', href: '/librarian/settings', icon: Settings },
    ],
  },
];

const LibrarianSidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try { await logout(); toast.success('Logged out'); navigate('/'); }
    catch { toast.error('Failed to logout'); }
  };

  const name = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : user?.username ?? 'Librarian';
  const initials = user?.first_name && user?.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`
    : (user?.username?.substring(0, 2) ?? 'LB').toUpperCase();

  return (
    <aside className="h-screen bg-[var(--lib-sidebar)] border-r border-[var(--lib-hairline)] flex flex-col">
      {/* Header */}
      <div className="h-16 px-4 flex items-center gap-3 border-b border-[var(--lib-hairline)]">
        <img src="/logo_stmarys.png" alt="" className="h-7 w-7" />
        <div>
          <p className="font-display font-semibold text-sm text-[var(--lib-ink)] leading-none">Library</p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mt-1">SMCHS</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-auto px-3 py-5 space-y-6">
        {NAV_GROUPS.map((g) => (
          <div key={g.num}>
            <LibKicker num={g.num} label={g.label} className="px-2 mb-2" />
            <ul className="space-y-0.5">
              {g.items.map((it) => (
                <li key={it.href}>
                  <NavLink
                    to={it.href}
                    end={(it as any).exact}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 h-10 pl-4 pr-3 rounded-md font-display text-[13px] transition-colors ${
                        isActive
                          ? 'bg-[var(--lib-surface-2)] text-[var(--lib-ink)] font-medium'
                          : 'text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] hover:bg-[var(--lib-surface-2)]/60'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-2 bottom-2 w-0.5 bg-[var(--lib-gold)] rounded-full" />
                        )}
                        <it.icon className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{it.name}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-[var(--lib-hairline)] p-3 flex items-center gap-3 group">
        <div className="w-8 h-8 rounded-full bg-[var(--lib-navy)] text-[var(--lib-sidebar)] flex items-center justify-center font-display font-semibold text-[11px]">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display text-[12px] text-[var(--lib-ink)] truncate font-medium">{name}</p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Librarian</p>
        </div>
        <button
          onClick={handleLogout}
          title="Log out"
          className="w-7 h-7 rounded-md text-[var(--lib-ink-muted)] hover:text-[var(--lib-danger)] hover:bg-[var(--lib-surface-2)] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
};

export default LibrarianSidebar;
